import type React from 'react';

import { Accordion, Text } from '@change/ds-core-ui';
import { CaretDownIcon, CaretUpIcon, CheckmarkVerifiedIcon } from '@change/ds-icons';

import { Translate, TranslatePlural, useI18n } from '@change-corgi/core-react/i18n';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { usePetitionSignatureCount } from '#src/app/pages/petitionDetails/shared/hooks';
import { useCampaignTracking } from '#src/app/shared/hooks/campaignTracking';

import { SignatureThermometer } from './SignatureThermometer';

export const SignatureStatsActiveContainer: React.FC<EmptyProps> = () => {
	const { translatePlural } = useI18n();
	const { signatureCount, signatureGoal } = usePetitionSignatureCount();
	const { track } = useCampaignTracking();
	const {
		data: {
			petition: { id },
		},
	} = usePetitionDetailsPageData();

	const onToggle = (isExpanded: boolean) => {
		const trackingData = {
			petition_id: id,
			opened: isExpanded,
		};

		track('campaign_action_show_verified_signers_text_click', trackingData);
	};

	const showSignatureThermometer = signatureCount.displayed < 1000;

	const signatureCountHeadingLabel = translatePlural(
		'corgi.petition_gamma.details.verified_signers_text.signature_count_heading',
		signatureCount.displayed,
		{ countLocalized: signatureCount.displayedLocalized },
	);

	return (
		<div>
			<div className="mb-2 flex flex-col items-center justify-center gap-2">
				<div className="flex flex-row gap-1">
					<Text
						as="h2"
						variant="heading"
						size="large"
						aria-label={signatureCountHeadingLabel}
						tabIndex={0}
					>
						{signatureCount.displayedLocalized}
					</Text>

					<CheckmarkVerifiedIcon className="text-icon-information" size="small" />
				</div>
			</div>
			<Accordion
				variant="un-contained"
				headingClassName="justify-center text-center my-0"
				className="justify-center text-center"
				data-testid="verified-signatures-toggle-text"
				title={
					<TranslatePlural
						value="corgi.petition_gamma.details.verified_signers_text.title"
						count={signatureCount.displayed}
					/>
				}
				onToggle={(_event, expanded) => onToggle(expanded)}
				collapseIcon={<CaretDownIcon />}
				expandIcon={<CaretUpIcon />}
			>
				<Text className="text-typography-strong text-center italic" size="small" data-testid="verified-signatures-text">
					<Translate value="corgi.petition_gamma.details.verified_signers_text.body" />
				</Text>
			</Accordion>

			{showSignatureThermometer && (
				<div className="pt-6">
					<SignatureThermometer signatureCount={signatureCount.displayed} nextMilestone={signatureGoal.displayed} />
				</div>
			)}
		</div>
	);
};
